import { AfterViewInit, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, map } from 'rxjs';
import { AdvancedDataSource } from 'src/app/controls/advanced-gridview/datasources/advanced-datasource';
import { AdvancedGridviewModel } from 'src/app/controls/advanced-gridview/models/advanced-gridview-model';
import { ColumnDefinition } from 'src/app/controls/advanced-gridview/models/column-definition';
import { ColumnListDefinition } from 'src/app/controls/advanced-gridview/models/column-list-definition';
import { ColumnType } from 'src/app/controls/advanced-gridview/models/column-type';
import { MenuITemAction } from 'src/app/controls/advanced-gridview/models/menu-item-action';
import { MenuItemEvent } from 'src/app/controls/advanced-gridview/models/menu-item-event';
import { SortInfos } from 'src/app/controls/advanced-gridview/models/sort-infos';
import { FilterType } from 'src/app/controls/filters/models/filter-type';
import { ListItem } from 'src/app/controls/filters/models/list-item';
import { DefaultListComponent } from 'src/app/controls/list/default-list/default-list.component';
import { EnumHelper } from 'src/app/helpers/enum-helper';
import { BoxGroup } from 'src/app/models/box-group';
import { Rights } from 'src/app/models/enums/rights';
import { ISortable } from 'src/app/models/interfaces/sortable.interface';
import { BoxGroupsService } from 'src/app/services/box-groups.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { ViewsService } from 'src/app/services/views.service';
import { AdvancedGridviewComponent } from '../../../controls/advanced-gridview/advanced-gridview.component';

@Component({
    selector: 'app-box-groups-list',
    templateUrl: './box-groups-list.component.html',
    styleUrls: ['./box-groups-list.component.scss'],
    imports: [AdvancedGridviewComponent]
})
export class BoxGroupsListComponent extends DefaultListComponent<BoxGroup> implements OnInit, AfterViewInit {
    /**
     * Configuration des colonnes de la liste.
     */
    tableConfig: ColumnDefinition[] = null;

    /**
     * Constructeur
     * @param dialog : Service pour ouvrir des boites de dialogue.
     * @param memoryService : Service pour récupérer les informations en mémoire.
     * @param boxGroupsService : Service des groupes de bannettes
     */
    constructor(
        public override dialog: MatDialog,
        protected override memoryService: MemoryService,
        private boxGroupsService: BoxGroupsService,
        private viewsService: ViewsService,
        public enumHelper: EnumHelper,
        private spinner: NgxSpinnerService,
        private notificationService: NotificationService
    ) {
        super(dialog, memoryService);

        // Specifie des colonnes en fonction des droits utilisateurs
        if (this.memoryService.getConnectedUser().params.idVisible === true) {
            this.tableConfig = [
                new ColumnDefinition('id', 'Id', ColumnType.Numeric, FilterType.Text, 1, 10, true),
                new ColumnDefinition('name', 'Nom', ColumnType.Text, FilterType.Text, 2, 50, true),
                new ColumnListDefinition('viewId', 'Vue', FilterType.List, 3, 25, true, false),
                new ColumnDefinition('order', 'Ordre', ColumnType.Numeric, FilterType.Numeric, 4, 10, true),
            ];
        } else {
            this.tableConfig = [
                new ColumnDefinition('name', 'Nom', ColumnType.Text, FilterType.Text, 2, 60, true),
                new ColumnListDefinition('viewId', 'Vue', FilterType.List, 3, 25, true, false),
                new ColumnDefinition('order', 'Ordre', ColumnType.Numeric, FilterType.Numeric, 4, 10, true),
            ];
        }

        this.AddMenuItem('Éditer', MenuITemAction.Update, Rights.BoxEdit);
        this.AddMenuItem('Supprimer', MenuITemAction.Delete, Rights.BoxDelete);
    }

    /**
     * Initialise le composant.
     */
    ngOnInit(): void {
        this.dataSource = new AdvancedDataSource(null, this.boxGroupsService, this.spinner, this.notificationService);
        this.filteredTableModel = new AdvancedGridviewModel(this.tableConfig, new SortInfos('order', 'asc'));

        // Ajout des listes déroulantes
        // attente de 500ms pour que la gridView ait le temps de se remplir.
        setTimeout(() => {
            this.loadListColumnFilterDatas();
        }, 500);
    }

    /**
     * Appellé lorsque l'utilisateur clique sur un élément du menu contextuel.
     * @param event : Événement retourné par le menu contextuel.
     */
    public onAction(event: MenuItemEvent<BoxGroup>): void {
        if (event.action == MenuITemAction.Read) {
            // Cas de la consultation.
            this.itemDisplayed.emit(event.context);
        } else if (event.action == MenuITemAction.Update) {
            // Cas de la mise à jour.
            this.itemEdited.emit(event.context);
        } else if (event.action == MenuITemAction.Delete) {
            // Cas de la suppression --> affichage de la boite de dialogue de confirmation.
            this.displayDeleteConfirmationDialog(`Vous êtes sur le point de supprimer le groupe de bannettes '${event.context.name}'.`, event.context);
        }
    }

    ngAfterViewInit() {
        this.gridView.getSortableItemsFunction = this.getSortablesItems.bind(this);
        // bind(this) permet de specifier que le mot clé this utilisé dans la fonction getSortablesItems utilisera l'instance de FolderStatesList et non pas l'instance de la gridview qui appelle la fonction.
    }

    public onItemsSorted(event: ISortable[]) {
        this.itemsSorted.emit(event);
    }

    protected getSortablesItems(item: BoxGroup): Observable<BoxGroup[]> {
        return this.boxGroupsService.List(null, null, null, null).pipe(
            map((x) => {
                x.value.sort((a, b) => {
                    return a.order < b.order ? -1 : 1;
                });
                return x.value;
            })
        );
    }

    /**
     * Charge les éléments des filtres des colonnes de type liste.
     */
    private loadListColumnFilterDatas(): void {
        this.viewsService.List(null, null, null, null).subscribe((x) => {
            var listItem = x.value.map<ListItem<string>>((v) => <ListItem<string>>{ value: v.id.toString(), text: v.name });
            this.loadColumn(
                this.filteredTableModel.displayedColumnsDefinitions.find(
                    (x) => this.enumHelper.areEquals(ColumnType, x.type, ColumnType.List) && x.columnName === 'viewId'
                ) as ColumnListDefinition<string>,
                listItem
            );
        });
    }
}
